import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node"
import { Link, useLoaderData } from "@remix-run/react"
import { ArrowLeftIcon, CalendarIcon, ClockIcon } from "lucide-react"
import { PageBodyWithClassName, PageContainer } from "#app/components/ui/layout"
import { Separator } from "#app/components/ui/separator.tsx"
import { type BlogType, getEntryBySlug } from "#app/utils/platforms/contentful.server.ts"
import { getSocialMetas } from "#app/utils/seo.ts"

const Bold = ({ children }: { children: any}) => <p className="">{children}</p>;

const Heading2 = ({ children }: { children: any}) => <h2 className="text-2xl font-bold mt-8 mb-4">
{children}
</h2>
const Heading1 = ({ children }: { children: any}) => <h2 className="text-2xl font-bold mt-8 mb-4">
{children}
</h2>
const Heading3 = ({ children }: { children: any}) => <h2 className="text-xl font-bold mt-8 mb-4">
{children}
</h2>

const Paragraph = ({ children }: { children: any}) => <p className="text-xl mb-4">
{children}
</p>

const options = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => <Paragraph>{children}</Paragraph>,
    [BLOCKS.HEADING_1]: (node: any, children: any) => <Heading1>{children}</Heading1>,
    [BLOCKS.HEADING_2]: (node: any, children: any) => <Heading2>{children}</Heading2>,
    [BLOCKS.HEADING_3]: (node: any, children: any) => <Heading3>{children}</Heading3>,
    [INLINES.HYPERLINK]: (node: any, children: any) => <a href={node.data.uri} className="text-xl text-blue-500" target="_blank" rel="noreferrer">{children}</a>
  },
  renderText: (text: string) => text,
};

export const meta: MetaFunction<typeof loader> = ({ data, params}) => {
  const slug = params.slug ?? ''

  return [
    { viewport: 'width=device-width,initial-scale=1,viewport-fit=cover' },
    ...getSocialMetas({
      keywords: 'fixed deposit blog, finance blogs',
      url: `https://www.superfd.in/blog/${slug}`,
      image: `https:${data?.image.fields.file.url}`,
      title: `${data?.title} | Super FD`,
      description: data?.description,
    }),
  ]
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
   const data = await getEntryBySlug(params.slug ?? '')

   const blogData = {
    ...data.fields
   } as BlogType

   return blogData
}

export default function BlogContentRoute() {
    const post = useLoaderData<typeof loader>()
    
    return (
      <PageBodyWithClassName className="pt-0">
      <PageContainer className="px-0">

      <section className="pt-12 pb-12 lg:pt-24 lg:pb-12">

      <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm mb-8 hover:text-primary transition-colors">
          <ArrowLeftIcon size={16} />
          Back to all posts
        </Link>

        {/* Post header */}
        <header className="mb-8">
          {/* <Badge variant="secondary" className="mb-4">
            {post.category}
          </Badge> */}
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">{post.title}</h1>

          <div className="flex flex-wrap items-center gap-4 mt-6">
            {/* <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={post.author.avatar} alt={post.author.name} />
                <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium">{post.author.name}</div>
                <div className="text-sm text-muted-foreground">{post.author.role}</div>
              </div>
            </div> */}

            {/* <Separator orientation="vertical" className="h-6" /> */}

            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <CalendarIcon size={14} />
              <span>{post.date}</span>
            </div>

            <Separator orientation="vertical" className="h-6" />

            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <ClockIcon size={14} />
              <span>{post.readTime} min read</span>
            </div>
          </div>
        </header>

        {/* Featured image */}
        <div className="relative aspect-video w-full overflow-hidden rounded-lg mb-10">
          <img src={post.image.fields.file.url ? `https:${post.image.fields.file.url}` : ''} alt={post.title} className="object-cover"/>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          {
            documentToReactComponents(post.content, options)
          }
        </div>

      </div>
    </div>


      </section>

      </PageContainer>
      </PageBodyWithClassName>
    )
}
